import React, { useState, useEffect, useRef } from 'react';
import { FaChevronDown, FaChevronRight, FaFutbol, FaBasketballBall, FaVolleyballBall } from 'react-icons/fa';
import { GiTennisRacket } from 'react-icons/gi';
import axios from 'axios';
import { verifySportsAppointment } from '../../services/sportsAppointmentService';
import './SportsAppointments.scss';

const API_BASE_URL = 'https://api.iskenderun.bel.tr/api';

const facilityIcons = {
  Tenis: GiTennisRacket,
  Futbol: FaFutbol,
  Basketbol: FaBasketballBall,
  Voleybol: FaVolleyballBall,
  'Basketbol/Voleybol': FaBasketballBall,
};

const SportsAppointments = () => {
  const formRef = useRef(null);
  const [regions, setRegions] = useState([]);
  const [expandedRegions, setExpandedRegions] = useState([]);
  const [selectedFacility, setSelectedFacility] = useState(null);
  const [selectedDate, setSelectedDate] = useState(() => {
    const today = new Date();
    today.setMinutes(today.getMinutes() - today.getTimezoneOffset());
    return today.toISOString().split('T')[0];
  });
  const [selectedTimeSlot, setSelectedTimeSlot] = useState(null);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    idNumber: '',
    email: '',
    phone: '',
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [appointmentDetail, setAppointmentDetail] = useState(null);
  const [appointments, setAppointments] = useState([]);

  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [pendingAppointmentId, setPendingAppointmentId] = useState(null);
  const [verificationError, setVerificationError] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);

  useEffect(() => {
    fetchRegionsAndFacilities();
    fetchAllAppointments();
  }, []);

  useEffect(() => {
    if (selectedFacility) {
      fetchAvailableTimeSlots(selectedFacility.id, selectedDate);
    }
  }, [selectedDate, selectedFacility?.id]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      fetchAllAppointments();

      if (selectedFacility) {
        fetchAvailableTimeSlots(selectedFacility.id, selectedDate);
      }
    }, 20000);

    return () => clearInterval(intervalId);
  }, [selectedFacility, selectedDate]);

  const fetchRegionsAndFacilities = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/sports/regions-with-facilities`);
      setRegions(response.data?.data || []);
      setError(null);
    } catch (err) {
      console.error('Bölgeler yüklenirken hata:', err);
      setError('Veriler yüklenirken bir hata oluştu. Lütfen tekrar deneyin.');
    } finally {
      setLoading(false);
    }
  };

  const fetchAllAppointments = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/sports/appointments`);
      setAppointments(response.data?.data || []);
    } catch (err) {
      console.error('Randevular yüklenirken hata:', err);
    }
  };

  const fetchAvailableTimeSlots = async (facilityId, date) => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/sports/available-slots/${facilityId}`,
        { params: { date } }
      );

      setSelectedFacility(prev => ({
        ...prev,
        timeSlots: response.data?.data || []
      }));
    } catch (err) {
      console.error('Saat dilimleri yüklenirken hata:', err);
    }
  };

  const handleCreateAppointment = async () => {
    if (!formData.firstName || !formData.lastName || !formData.idNumber || !formData.email || !formData.phone) {
      alert('Lütfen tüm zorunlu alanları doldurunuz.');
      return;
    }

    try {
      const appointmentData = {
        facilityId: selectedFacility.id,
        appointmentDate: selectedDate,
        startTime: selectedTimeSlot.time.split(' - ')[0],
        endTime: selectedTimeSlot.time.split(' - ')[1],
        firstName: formData.firstName,
        lastName: formData.lastName,
        identityNumber: formData.idNumber,
        email: formData.email,
        phone: formData.phone,
        createdByWeb: false,
      };

      await axios.post(`${API_BASE_URL}/sports/appointments`, appointmentData);

      alert(`Randevu başarıyla oluşturuldu!\n\nAd Soyad: ${formData.firstName} ${formData.lastName}\nSaha: ${selectedFacility.name}\nTarih: ${new Date(selectedDate).toLocaleDateString('tr-TR')}\nSaat: ${selectedTimeSlot.time}`);

      setFormData({ firstName: '', lastName: '', idNumber: '', email: '', phone: '' });
      setSelectedTimeSlot(null);
      fetchAvailableTimeSlots(selectedFacility.id, selectedDate);
      fetchAllAppointments();
    } catch (err) {
      console.error('Randevu oluşturulurken hata:', err);
      alert('Randevu oluşturulurken bir hata oluştu. Lütfen tekrar deneyin.');
    }
  };

  const toggleRegion = (regionId) => {
    setExpandedRegions(prev => {
      if (prev.includes(regionId)) {
        return prev.filter(id => id !== regionId);
      } else {
        return [...prev, regionId];
      }
    });
  };

  const handleFacilityClick = async (facility, region) => {
    setSelectedFacility({
      ...facility,
      regionName: region.name,
      timeSlots: []
    });
    setSelectedTimeSlot(null);

    await fetchAvailableTimeSlots(facility.id, selectedDate);
  };

  const handleTimeSlotClick = async (slot) => {
    if (slot.available) {
      setSelectedTimeSlot(slot);
      setTimeout(() => {
        formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
    } else {
      try {
        const startTime = slot.time.split(' - ')[0];
        const response = await axios.get(
          `${API_BASE_URL}/sports/appointment-details/${selectedFacility.id}`,
          { params: { date: selectedDate, startTime } }
        );
        if (response.data?.data) {
          setAppointmentDetail(response.data.data);
          setShowDetailModal(true);
        }
      } catch (err) {
        console.error('Randevu detayları yüklenirken hata:', err);
        alert('Randevu detayları yüklenirken hata oluştu.');
      }
    }
  };

  if (loading) {
    return (
      <div className="sports-appointments">
        <div className="page-header">
          <h2>Spor Randevu Sistemi</h2>
        </div>
        <div className="loading-message">
          <p>Yükleniyor...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="sports-appointments">
        <div className="page-header">
          <h2>Spor Randevu Sistemi</h2>
        </div>
        <div className="error-message">
          <p>{error}</p>
          <button onClick={fetchRegionsAndFacilities} className="retry-button">Tekrar Dene</button>
        </div>
      </div>
    );
  }

  return (
    <div className="sports-appointments">
      <div className="page-header">
        <h2>Spor Randevu Sistemi</h2>
      </div>

      <div className="sports-content">
        <div className="left-panel">
          <div className="regions-list">
            {regions.map(region => (
              <div key={region.id} className="region-item">
                <button
                  className={`region-header ${expandedRegions.includes(region.id) ? 'expanded' : ''}`}
                  onClick={() => toggleRegion(region.id)}
                >
                  <span className="region-icon">
                    {expandedRegions.includes(region.id) ? <FaChevronDown /> : <FaChevronRight />}
                  </span>
                  <span className="region-name">{region.name}</span>
                  <span className="region-count">{region.facilities.length} Saha</span>
                </button>

                {expandedRegions.includes(region.id) && (
                  <div className="facilities-list">
                    {region.facilities.map(facility => {
                      const Icon = facilityIcons[facility.type] || FaFutbol;
                      const isSelected = selectedFacility?.id === facility.id;

                      return (
                        <button
                          key={facility.id}
                          className={`facility-card ${isSelected ? 'selected' : ''}`}
                          onClick={() => handleFacilityClick(facility, region)}
                        >
                          <div className="facility-icon">
                            <Icon />
                          </div>
                          <div className="facility-info">
                            <div className="facility-name">{facility.name}</div>
                            <div className="facility-meta">
                              <span className="facility-type">{facility.type}</span>
                              <span className="facility-capacity">{facility.capacity}</span>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="right-panel">
          {selectedFacility ? (
            <div className="facility-details">
              <div className="facility-details-header">
                <div className="facility-title">
                  <h3>{selectedFacility.name}</h3>
                  <span className="facility-region">{selectedFacility.regionName}</span>
                </div>
              </div>

              <div className="facility-specs">
                <div className="spec-item">
                  <span className="spec-label">Tür</span>
                  <span className="spec-value">{selectedFacility.type}</span>
                </div>
                <div className="spec-item">
                  <span className="spec-label">Kapasite</span>
                  <span className="spec-value">{selectedFacility.capacity}</span>
                </div>
              </div>

              <div className="appointment-section">
                <div className="date-picker-section">
                  <label>Tarih Seçin</label>
                  <input
                    type="date"
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                    className="date-input"
                  />
                </div>

                <div className="time-slots-section">
                  <h4>Müsait Saatler</h4>
                  <div className="time-slots-grid">
                    {selectedFacility.timeSlots.map((slot, index) => (
                      <button
                        key={index}
                        className={`time-slot ${slot.available ? 'available' : (slot.status?.toLowerCase() || 'occupied')} ${selectedTimeSlot?.time === slot.time ? 'selected' : ''}`}
                        onClick={() => handleTimeSlotClick(slot)}
                      >
                        <span className="slot-time">{slot.time}</span>
                        <span className="slot-status">
                          {slot.available ? 'Müsait' : 'Dolu'}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>

                {selectedTimeSlot && (
                  <div className="booking-form" ref={formRef}>
                    <h4>Manuel Randevu Oluştur</h4>
                    <div className="form-grid">
                      <div className="form-group">
                        <label htmlFor="firstName">Ad <span className="required">*</span></label>
                        <input
                          id="firstName"
                          type="text"
                          value={formData.firstName}
                          onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                          placeholder="Adını giriniz"
                          className="form-input"
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="lastName">Soyad <span className="required">*</span></label>
                        <input
                          id="lastName"
                          type="text"
                          value={formData.lastName}
                          onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                          placeholder="Soyadını giriniz"
                          className="form-input"
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="idNumber">T.C. Kimlik No <span className="required">*</span></label>
                        <input
                          id="idNumber"
                          type="text"
                          value={formData.idNumber}
                          onChange={(e) => setFormData({ ...formData, idNumber: e.target.value.replace(/\D/g, '').slice(0, 11) })}
                          placeholder="TC Kimlik No giriniz"
                          className="form-input"
                          maxLength="11"
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="email">E-Posta <span className="required">*</span></label>
                        <input
                          id="email"
                          type="email"
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          placeholder="E-posta adresi"
                          className="form-input"
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="phone">Telefon <span className="required">*</span></label>
                        <input
                          id="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                          placeholder="5XXXXXXXXX"
                          className="form-input"
                        />
                      </div>

                      <div className="form-group appointment-summary">
                        <label>Randevu Bilgileri</label>
                        <div className="summary-box">
                          <div className="summary-item">
                            <strong>Saha:</strong> {selectedFacility.name}
                          </div>
                          <div className="summary-item">
                            <strong>Tarih:</strong> {new Date(selectedDate).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' })}
                          </div>
                          <div className="summary-item">
                            <strong>Saat:</strong> {selectedTimeSlot.time}
                          </div>
                        </div>
                      </div>
                    </div>

                    <button
                      type="button"
                      className="submit-button"
                      onClick={handleCreateAppointment}
                    >
                      Randevu Kaydet
                    </button>
                  </div>
                )}

                <div className="demo-notice">
                  <strong>Not:</strong> Randevu sistemi aktif. Oluşturulan randevular veritabanına kaydedilecektir.
                </div>
              </div>
            </div>
          ) : (
            <div className="no-selection">
              <FaFutbol className="no-selection-icon" />
              <h3>Saha Seçin</h3>
              <p>Randevu almak için sol panelden bölge ve saha seçiniz</p>
            </div>
          )}
        </div>
      </div>

      <div className="appointments-list-section">
        <div className="section-header">
          <h3>Alınan Randevular</h3>
          <span className="appointment-count">{appointments.length} randevu</span>
        </div>
        <div className="appointments-table-wrapper">
          <table className="appointments-table">
            <thead>
              <tr>
                <th>AD SOYAD</th>
                <th>TC</th>
                <th>TARİH</th>
                <th>SAAT</th>
                <th>TESİS</th>
                <th>DURUM</th>
                <th>E-POSTA</th>
                <th>TELEFON</th>
                <th>OLUŞTURMA</th>
              </tr>
            </thead>
            <tbody>
              {appointments.length === 0 ? (
                <tr>
                  <td colSpan="9" className="no-data">Henüz randevu bulunmamaktadır.</td>
                </tr>
              ) : (
                appointments.map((appointment, index) => (
                  <tr key={appointment.id || index}>
                    <td className="name-cell">{appointment.firstName} {appointment.lastName}</td>
                    <td>{appointment.identityNumber}</td>
                    <td>{new Date(appointment.appointmentDate).toLocaleDateString('tr-TR')}</td>
                    <td>{appointment.startTime} - {appointment.endTime}</td>
                    <td>{appointment.facilityName}</td>
                    <td>
                      <span className={`status-badge ${appointment.status?.toLowerCase()}`}>
                        {appointment.status === 'Pending' ? 'BEKLİYOR' :
                          appointment.status === 'Confirmed' ? 'ONAYLANDI' :
                            appointment.status === 'Cancelled' ? 'İPTAL' : appointment.status}
                      </span>
                    </td>
                    <td className="email-cell">{appointment.email}</td>
                    <td>{appointment.phone}</td>
                    <td>{new Date(appointment.createdDate).toLocaleString('tr-TR')}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showDetailModal && appointmentDetail && (
        <div className="appointment-modal-overlay" onClick={() => setShowDetailModal(false)}>
          <div className="appointment-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Randevu Detayları</h3>
              <button className="modal-close" onClick={() => setShowDetailModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="detail-row">
                <span className="detail-label">Ad Soyad:</span>
                <span className="detail-value">{appointmentDetail.firstName} {appointmentDetail.lastName}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">TC Kimlik:</span>
                <span className="detail-value">{appointmentDetail.identityNumber}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Telefon:</span>
                <span className="detail-value">{appointmentDetail.phone}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">E-posta:</span>
                <span className="detail-value">{appointmentDetail.email}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Tesis:</span>
                <span className="detail-value">{appointmentDetail.facilityName}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Tarih:</span>
                <span className="detail-value">{new Date(appointmentDetail.appointmentDate).toLocaleDateString('tr-TR')}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Saat:</span>
                <span className="detail-value">{appointmentDetail.startTime} - {appointmentDetail.endTime}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Durum:</span>
                <span className={`detail-status ${appointmentDetail.status?.toLowerCase()}`}>
                  {appointmentDetail.status === 'Pending' ? 'BEKLİYOR' :
                    appointmentDetail.status === 'Confirmed' ? 'ONAYLANDI' :
                      appointmentDetail.status === 'Cancelled' ? 'İPTAL EDİLDİ' :
                        appointmentDetail.status === 'Completed' ? 'TAMAMLANDI' : appointmentDetail.status}
                </span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Oluşturulma:</span>
                <span className="detail-value">{new Date(appointmentDetail.createdDate).toLocaleString('tr-TR')}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Kaynak:</span>
                <span className="detail-value">{appointmentDetail.createdByWeb ? 'Web Sitesi' : 'Admin Panel'}</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SportsAppointments;
